import { forwardRef, useEffect, useId, useRef } from 'react';
import { cn, focusRing } from '../utils/cn';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  hideLabel?: boolean;
  description?: string;
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hideLabel, description, indeterminate, className, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const descriptionId = description ? `${controlId}-description` : undefined;
  const innerRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);

  return (
    <div className={cn('flex items-start gap-2', className)}>
      <input
        ref={(node) => {
          innerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        id={controlId}
        type="checkbox"
        aria-describedby={descriptionId}
        aria-label={hideLabel ? label : undefined}
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded-sm border border-line-strong bg-surface',
          'accent-[var(--color-action)] disabled:cursor-not-allowed disabled:bg-surface-sunken',
          focusRing,
        )}
        {...rest}
      />
      {hideLabel ? null : (
        <div className="flex flex-col">
          <label htmlFor={controlId} className="cursor-pointer text-dense-lg text-content">
            {label}
          </label>
          {description ? (
            <span id={descriptionId} className="text-dense text-content-muted">
              {description}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
});
